import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import SlotPicker from "../components/SlotPicker";
import SpiritualDisclaimer from "../components/SpiritualDisclaimer";

export default function BookReading() {
  const [services, setServices] = useState([]);
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    service_id: params.get("service") || "",
    name: "", email: "", phone: "",
    preferred_date: "", preferred_time: "",
    mode: "online",
    payment_plan: "full",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => {
      const eligible = r.data.filter((s) => s.category !== "House Calls" && s.price > 0);
      setServices(eligible);
      if (!form.service_id && eligible[0]) setForm((f) => ({ ...f, service_id: eligible[0].id }));
    });
    // eslint-disable-next-line
  }, []);

  const selected = services.find((s) => s.id === form.service_id);
  const DEPOSIT_ELIGIBLE = ["tarot-pastlife", "tarot-ancestral"];
  const depositEligible = selected && DEPOSIT_ELIGIBLE.includes(selected.id);
  const fullPrice = selected ? selected.price : 0;
  const depositAmount = depositEligible && form.payment_plan === "deposit" ? fullPrice / 2 : fullPrice;
  const balanceDue = fullPrice - depositAmount;
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Please select a service");
    if (!form.preferred_date || !form.preferred_time) return toast.error("Please pick a date and time slot");
    if (!agreed) return toast.error("Please agree to the Terms & Disclaimer to continue");
    setLoading(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        ...form, service_name: selected.name, price: selected.price,
      });
      const { data: checkout } = await api.post("/checkout/session", {
        item_type: "booking", item_id: booking.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  return (
    <div className="py-20 md:py-28 px-6 md:px-10 max-w-5xl mx-auto" data-testid="book-reading-page">
      <div className="overline mb-4">Bookings</div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Book a Reading</h1>
      <p className="mt-6 text-white/70 max-w-2xl text-lg">
        Choose your reading, pick a date and time, and secure your session. Online and in-person available.
      </p>

      <SpiritualDisclaimer />

      <form onSubmit={submit} className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" data-testid="booking-form">
        <div className="md:col-span-2">
          <label className="overline block mb-3">Service</label>
          <select
            name="service_id"
            value={form.service_id}
            onChange={(e) => setForm({ ...form, service_id: e.target.value, preferred_date: "", preferred_time: "", payment_plan: "full" })}
            className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#D4AF37] outline-none"
            data-testid="booking-service-select"
          >
            {services.map((s) => (
              <option key={s.id} value={s.id} className="bg-black">
                {s.name} — {s.duration} — ${s.price.toFixed(2)}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="overline block mb-3">Session Mode *</label>
          <div className="grid grid-cols-3 gap-3" data-testid="booking-mode">
            {[
              { id: "online", label: "Online (Video)" },
              { id: "phone", label: "Phone" },
              { id: "in-person", label: "In Person" },
            ].map((m) => (
              <label key={m.id} className={`border p-3 cursor-pointer text-center text-xs uppercase tracking-[0.18em] transition-colors ${form.mode === m.id ? "border-[#D4AF37] bg-[#D4AF37]/5 text-[#D4AF37]" : "border-white/15 text-white/70 hover:border-[#D4AF37]/50"}`} data-testid={`booking-mode-${m.id}`}>
                <input type="radio" name="mode" value={m.id} checked={form.mode === m.id} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="sr-only" />
                {m.label}
              </label>
            ))}
          </div>
        </div>

        {depositEligible && (
          <div className="md:col-span-2">
            <label className="overline block mb-3">Payment Plan</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="booking-payment-plan">
              {[
                { id: "full", label: "Pay in Full", sub: `$${fullPrice.toFixed(2)} now` },
                { id: "deposit", label: "Pay 50% Deposit", sub: `$${(fullPrice / 2).toFixed(2)} now · $${(fullPrice / 2).toFixed(2)} at session` },
              ].map((p) => (
                <label key={p.id} className={`border p-4 cursor-pointer transition-colors ${form.payment_plan === p.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/15"}`} data-testid={`booking-plan-${p.id}`}>
                  <input type="radio" name="payment_plan" value={p.id} checked={form.payment_plan === p.id} onChange={(e) => setForm({ ...form, payment_plan: e.target.value })} className="sr-only" />
                  <div className="font-serif text-lg">{p.label}</div>
                  <div className="text-xs text-white/60 mt-1">{p.sub}</div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="md:col-span-2">
          <SlotPicker
            serviceId={form.service_id}
            value={{ date: form.preferred_date, time: form.preferred_time }}
            onChange={(v) => setForm({ ...form, preferred_date: v.date, preferred_time: v.time })}
          />
        </div>

        <Field label="Full Name" name="name" value={form.name} onChange={onChange} testid="booking-name" required />
        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="booking-email" required />
        <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="booking-phone" />

        <div className="md:col-span-2">
          <label className="overline block mb-3">Additional Notes</label>
          <textarea
            name="notes" value={form.notes} onChange={onChange} rows={4}
            className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#D4AF37] outline-none"
            placeholder="Share any context you'd like The Lion Chief to know."
            data-testid="booking-notes"
          />
        </div>

        <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-white/10">
          <div>
            <label className="flex items-start gap-3 text-sm text-white/70 cursor-pointer" data-testid="booking-agree">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" data-testid="booking-agree-checkbox" />
              <span>I agree to the <a href="/terms" target="_blank" className="text-[#D4AF37] underline">Terms</a>, <a href="/cancellation-policy" target="_blank" className="text-[#D4AF37] underline">Cancellation Policy</a>, and <a href="/disclaimer" target="_blank" className="text-[#D4AF37] underline">Spiritual Services Disclaimer</a>.</span>
            </label>
          </div>
        </div>

        <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-white/10">
          <div>
            <div className="overline text-[#D4AF37]">Due Now</div>
            <div className="font-serif text-3xl">${depositAmount.toFixed(2)}</div>
            {balanceDue > 0 && (
              <div className="text-sm text-[#D4AF37] mt-1" data-testid="booking-balance-due">Balance due at session: ${balanceDue.toFixed(2)}</div>
            )}
            {form.preferred_date && form.preferred_time && (
              <div className="text-sm text-white/60 mt-1">{form.preferred_date} at {form.preferred_time}</div>
            )}
          </div>
          <button
            type="submit" disabled={loading}
            className="bg-[#8B0000] hover:bg-[#A50000] text-white px-10 py-4 text-xs uppercase tracking-[0.2em]"
            data-testid="booking-submit-btn"
          >
            {loading ? "Processing…" : "Continue to Payment"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="overline block mb-3">{label}{required && " *"}</label>
      <input
        type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder}
        className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none"
        data-testid={testid}
      />
    </div>
  );
}
